import React, { useContext } from "react";
import {
    Container,
    Box,
    Typography,
    Button,
    Grid,
    Card,
    CardContent,
    useTheme,
    useMediaQuery,
    Chip,
} from "@mui/material";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import FlashOnIcon from "@mui/icons-material/FlashOn";
import SearchIcon from "@mui/icons-material/Search";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SavingsOutlinedIcon from "@mui/icons-material/SavingsOutlined";
import Groups2Icon from "@mui/icons-material/Groups2";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useNavigate, useLocation } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import { useTranslation } from "react-i18next";
import { userHasActiveEntitlement } from "../../utils/entitlements";
import { Helmet } from "react-helmet-async";
import { FRONTEND_URL } from "../../config/urls";
import { getPathHreflangLinks } from "../../seo/hreflang";
import { useLocationCtx } from "../../context/LocationContext";
import { resolveCountryId } from "../../utils/country";
import { getLastShoppingEntryPath } from "../../utils/shoppingEntry";

const ClientLandingPage = () => {
    const { user } = useContext(AuthContext);
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const location = useLocation();
    const match = location.pathname.match(/^\/([a-z]{2}(?:-[a-z]{2})?)\b/);
    const prefix = match ? `/${match[1]}` : ""
    const isPremium = userHasActiveEntitlement(user);
    const { country: ctxCountry } = useLocationCtx();
    const isUSMarket = resolveCountryId({ locationCountry: ctxCountry }).id === "2";
    const isEnglishUI = String(i18n.language || "").toLowerCase().startsWith("en");
    const heroLangOverride = isEnglishUI && isUSMarket ? "en-US" : null;
    const heroT = (key, fallback) =>
        heroLangOverride ? t(key, { lng: heroLangOverride, defaultValue: fallback }) : t(key, fallback);

    const handleGoPricing = () => navigate(`${prefix}/plans`);
    const handleGoMealPlan = () => navigate(`${prefix}/client/meal/plan`);
    const handleGoQuickRecipe = () => navigate(`${prefix}/quick-recipe`);
    const handleGoSearchRecipes = () => navigate(`${prefix}/search-recipes`);
    const handleGoBudgetRecipes = () => navigate(`${prefix}/budget-recipes`);
    const handleGoDashboard = () => navigate(`${prefix}/client/dashboard`);
    const handleGoShopping = () => {
        const locale = match ? match[1] : null;
        const resumePath = getLastShoppingEntryPath(locale, {
            isAuthenticated: Boolean(user?.id),
        });

        if (resumePath) {
            const legacyResumePath = resumePath.replace("/client/shopping/list", "/shopping-custom/list");
            navigate(legacyResumePath);
            return;
        }

        navigate(`${prefix}/shopping-custom/list`);
    };
    const handleGoMyRecipes = () => navigate(`${prefix}/client/recipes-user`);

    const pathWithoutLocale =
        (location.pathname || "/").replace(/^\/([a-z]{2}(?:-[a-z]{2})?)(?=\/|$)/i, "") || "/";

    return (
        <Box
            sx={{
                minHeight: "100vh",
                py: { xs: 3, sm: 6 },
                background: theme.palette.mode === "dark"
                    ? "radial-gradient(circle at top, #202938 0, #050608 55%)"
                    : "radial-gradient(circle at top, #f3fff7 0, #ffffff 55%)",
            }}
        >
            <Helmet>
                <link rel="canonical" href={`${FRONTEND_URL}${location.pathname || "/"}`} />
                {getPathHreflangLinks(pathWithoutLocale, "en")}
            </Helmet>
            <Container maxWidth="lg">
                {/* HERO SECTION */}
                <Grid
                    container
                    spacing={4}
                    alignItems="center"
                    sx={{ mb: { xs: 4, md: 6 } }}
                >
                    {/* Text side */}
                    <Grid item xs={12} md={7}>
                        <Chip
                            icon={<RestaurantIcon />}
                            label={t("smart_cooking_assistant") || "Your smart cooking assistant"}
                            sx={{
                                mb: 2,
                                fontWeight: 600,
                                borderRadius: 999,
                                bgcolor:
                                    theme.palette.mode === "dark"
                                        ? "rgba(41, 182, 121, 0.15)"
                                        : "rgba(46, 125, 50, 0.08)",
                            }}
                        />

                        <Typography
                            variant={isMobile ? "h4" : "h3"}
                            sx={{
                                fontWeight: 800,
                                lineHeight: 1.1,
                                mb: 2,
                                letterSpacing: "0.02em",
                            }}
                        >
                            {heroT("hero_h1", "Welcome to InCooking")}
                        </Typography>

                        <Typography
                            variant="subtitle1"
                            sx={{
                                maxWidth: 560,
                                mb: 3,
                                color:
                                    theme.palette.mode === "dark"
                                        ? "rgba(255,255,255,0.78)"
                                        : "text.secondary",
                            }}
                        >
                            {heroT(
                                "hero_subheadline",
                                "Plan your meals, control your budget and discover recipes tailored to your preferences — all in one smart kitchen hub."
                            )}
                        </Typography>

                        <Box sx={{ mb: 3, maxWidth: 520 }}>
                            {/* GUEST χρήστης */}
                            {!user && (
                                <Card
                                    sx={{
                                        borderRadius: 3,
                                        boxShadow: 2,
                                        border: (th) =>
                                            th.palette.mode === "dark"
                                                ? "1px solid rgba(255,255,255,0.08)"
                                                : "1px solid rgba(0,0,0,0.06)",
                                        background:
                                            theme.palette.mode === "dark"
                                                ? "rgba(15, 23, 42, 0.9)"
                                                : "rgba(232, 245, 233, 0.9)",
                                    }}
                                >
                                    <CardContent>
                                        <Typography
                                            variant="subtitle2"
                                            sx={{ fontWeight: 700, mb: 0.5 }}
                                        >
                                            {t("landing_guest_plan_title") ||
                                                "Χρησιμοποιείς το InCooking ως επισκέπτης"}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{ mb: 1.5 }}
                                            color="text.secondary"
                                        >
                                            {t("landing_guest_plan_body") ||
                                                "Μπορείς να δοκιμάσεις τις βασικές δυνατότητες δωρεάν. Δημιούργησε λογαριασμό και δες τα διαθέσιμα πλάνα για να ξεκλειδώσεις Premium εργαλεία και χρήση χωρίς διαφημίσεις."}
                                        </Typography>
                                        <Button
                                            variant="contained"
                                            size="small"
                                            onClick={handleGoPricing}
                                            sx={{ textTransform: "none", fontWeight: 600 }}
                                        >
                                            {t("see_plans_cta") || "Δες πλάνα & τιμές"}
                                        </Button>
                                    </CardContent>
                                </Card>
                            )}

                            {/* Συνδεδεμένος χρήστης με Free plan */}
                            {user && !isPremium && (
                                <Card
                                    sx={{
                                        borderRadius: 3,
                                        boxShadow: 2,
                                        border: (th) =>
                                            th.palette.mode === "dark"
                                                ? "1px solid rgba(255,255,255,0.1)"
                                                : "1px solid rgba(56, 142, 60, 0.35)",
                                        background:
                                            theme.palette.mode === "dark"
                                                ? "linear-gradient(135deg, #102a43, #0b1726)"
                                                : "linear-gradient(135deg, #e8f5e9, #e3f2fd)",
                                    }}
                                >
                                    <CardContent>
                                        <Typography
                                            variant="subtitle2"
                                            sx={{ fontWeight: 700, mb: 0.5 }}
                                        >
                                            {t("landing_free_plan_title") ||
                                                "Χρησιμοποιείς το Free πλάνο του InCooking"}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{ mb: 1.5 }}
                                            color="text.secondary"
                                        >
                                            {t("landing_free_plan_body") ||
                                                "Έχεις πρόσβαση στα βασικά εργαλεία για συνταγές, meal plan και λίστα αγορών. Με Premium Pro αφαιρούνται οι διαφημίσεις και ξεκλειδώνεις επιπλέον εργαλεία για budget, κόστη ανά σούπερ μάρκετ και πιο προηγμένα reports."}
                                        </Typography>
                                        <Button
                                            variant="contained"
                                            size="small"
                                            onClick={handleGoPricing}
                                            sx={{ textTransform: "none", fontWeight: 600 }}
                                        >
                                            {t("upgrade_to_premium_cta") || "Δες Premium πλάνα"}
                                        </Button>
                                    </CardContent>
                                </Card>
                            )}

                            {/* Premium χρήστης – αν θες, δείχνουμε μια ήρεμη επιβεβαίωση */}
                            {user && isPremium && (
                                <Card
                                    sx={{
                                        borderRadius: 3,
                                        boxShadow: 1,
                                        border: (th) =>
                                            th.palette.mode === "dark"
                                                ? "1px solid rgba(76, 175, 80, 0.7)"
                                                : "1px solid rgba(76, 175, 80, 0.4)",
                                        background:
                                            theme.palette.mode === "dark"
                                                ? "rgba(27, 94, 32, 0.25)"
                                                : "rgba(232, 245, 233, 0.9)",
                                    }}
                                >
                                    <CardContent sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
                                        <Chip
                                            label={t("premium_badge") || "Premium ενεργό"}
                                            color="success"
                                            size="small"
                                            sx={{ fontWeight: 700 }}
                                        />
                                        <Typography variant="body2" sx={{ flex: 1 }}>
                                            {t("landing_premium_plan_body") ||
                                                "Απολαμβάνεις την εφαρμογή χωρίς διαφημίσεις και με όλα τα Premium Pro εργαλεία κόστους & προγραμματισμού."}
                                        </Typography>
                                        <Button
                                            size="small"
                                            variant="text"
                                            onClick={handleGoPricing}
                                            sx={{ textTransform: "none", fontWeight: 500 }}
                                        >
                                            {t("manage_plan_cta") || "Διαχείριση πλάνου"}
                                        </Button>
                                    </CardContent>
                                </Card>
                            )}
                        </Box>
                        <Box
                            sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 1.5,
                                mb: 2,
                            }}
                        >
                            <Button
                                variant="contained"
                                size={isMobile ? "medium" : "large"}
                                startIcon={<RestaurantIcon />}
                                sx={{
                                    borderRadius: 999,
                                    px: 3.5,
                                    py: 1.1,
                                    fontWeight: 700,
                                    textTransform: "none",
                                }}
                                onClick={handleGoMealPlan}
                            >
                                {heroT("hero_cta_primary", "Start your meal plan")}
                            </Button>

                            <Button
                                variant="outlined"
                                size={isMobile ? "medium" : "large"}
                                startIcon={<FlashOnIcon />}
                                sx={{
                                    borderRadius: 999,
                                    px: 3,
                                    py: 1.05,
                                    fontWeight: 600,
                                    textTransform: "none",
                                    borderWidth: 2,
                                    "&:hover": { borderWidth: 2 },
                                }}
                                onClick={handleGoQuickRecipe}
                            >
                                {heroT("hero_cta_secondary", "1-ingredient recipe")}
                            </Button>

                            <Button
                                size={isMobile ? "medium" : "large"}
                                startIcon={<SearchIcon />}
                                sx={{
                                    borderRadius: 999,
                                    px: 2.4,
                                    py: 1.05,
                                    fontWeight: 600,
                                    textTransform: "none",
                                }}
                                onClick={handleGoSearchRecipes}
                            >
                                {t("search_recipes") || "Search recipes"}
                            </Button>
                        </Box>
                        <Typography
                            variant="caption"
                            sx={{
                                display: "block",
                                color: "text.secondary",
                                mt: 0.5,
                            }}
                        >
                            {heroT("hero_microcopy", "No credit card required — cancel anytime")}
                        </Typography>

                        <Box
                            sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 1.5,
                                mt: 1,
                            }}
                        >
                            {[
                                heroT("hero_bullet_1", "Smart meal planning"),
                                heroT("hero_bullet_2", "Optimized shopping list"),
                                heroT("hero_bullet_3", "Your own recipes in one place"),
                            ].map((item, idx) => (
                                <Box
                                    key={idx}
                                    sx={{ display: "flex", alignItems: "center", gap: 0.7 }}
                                >
                                    <CheckCircleIcon
                                        sx={{ fontSize: 20, color: theme.palette.success.main }}
                                    />
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            opacity: 0.9,
                                            fontSize: "0.95rem",
                                        }}
                                    >
                                        {item}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </Grid>

                    {/* Right / Illustration side */}
                    <Grid item xs={12} md={5}>
                        <Box
                            sx={{
                                borderRadius: 6,
                                p: 4,
                                background:
                                    theme.palette.mode === "dark"
                                        ? "linear-gradient(135deg, #1b2533, #283648)"
                                        : "linear-gradient(135deg, #e8f5e9, #e1f5fe)",
                                boxShadow:
                                    theme.palette.mode === "dark"
                                        ? "0 18px 55px rgba(0,0,0,0.6)"
                                        : "0 18px 55px rgba(76,175,80,0.25)",
                            }}
                        >
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <Typography
                                        variant="subtitle2"
                                        sx={{
                                            mb: 1,
                                            fontWeight: 700,
                                            textTransform: "uppercase",
                                            letterSpacing: "0.12em",
                                            fontSize: "0.78rem",
                                        }}
                                    >
                                        {t("today_shortcuts") || "Today’s shortcuts"}
                                    </Typography>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <Card
                                        onClick={handleGoMealPlan}
                                        sx={{
                                            borderRadius: 3,
                                            cursor: "pointer",
                                            height: "100%",
                                            "&:hover": {
                                                transform: "translateY(-3px)",
                                                boxShadow: 6,
                                            },
                                            transition: "all 0.18s",
                                        }}
                                    >
                                        <CardContent>
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    mb: 1,
                                                    gap: 1,
                                                }}
                                            >
                                                <RestaurantIcon
                                                    color="success"
                                                    sx={{ fontSize: 30 }}
                                                />
                                                <Typography variant="subtitle1" fontWeight={700}>
                                                    {t("meal_plan") || "Meal plan"}
                                                </Typography>
                                            </Box>
                                            <Typography variant="body2" color="text.secondary">
                                                {t("today_meal_plan_hint") ||
                                                    "See or generate your weekly plan in a few taps."}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <Card
                                        onClick={handleGoDashboard}
                                        sx={{
                                            borderRadius: 3,
                                            cursor: "pointer",
                                            height: "100%",
                                            "&:hover": {
                                                transform: "translateY(-3px)",
                                                boxShadow: 6,
                                            },
                                            transition: "all 0.18s",
                                        }}
                                    >
                                        <CardContent>
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    mb: 1,
                                                    gap: 1,
                                                }}
                                            >
                                                <ShoppingCartIcon color="primary" sx={{ fontSize: 30 }} />
                                                <Typography variant="subtitle1" fontWeight={700}>
                                                    {t("open_dashboard") || "Open dashboard"}
                                                </Typography>
                                            </Box>
                                            <Typography variant="body2" color="text.secondary">
                                                {t("dashboard_shortcut_hint") ||
                                                    "Access inventory, shopping list and more tools."}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>

                                <Grid item xs={12}>
                                    <Card
                                        onClick={handleGoSearchRecipes}
                                        sx={{
                                            mt: 1,
                                            borderRadius: 4,
                                            cursor: "pointer",
                                            "&:hover": {
                                                transform: "translateY(-3px)",
                                                boxShadow: 6,
                                            },
                                            transition: "all 0.18s",
                                        }}
                                    >
                                        <CardContent
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1.5,
                                            }}
                                        >
                                            <MenuBookIcon color="secondary" sx={{ fontSize: 28 }} />
                                            <Box>
                                                <Typography variant="subtitle1" fontWeight={700}>
                                                    {t("discover_recipes") || "Discover recipes"}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {t("discover_recipes_hint") ||
                                                        "Filter by ingredients, budget, cuisine and more."}
                                                </Typography>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>
                        </Box>
                    </Grid>
                </Grid>

                <Box
                    sx={{
                        mt: { xs: 2, md: 3 },
                        mb: { xs: 4, md: 6 },
                        borderRadius: 6,
                        p: { xs: 2.5, md: 4 },
                        background: theme.palette.mode === "dark"
                            ? "linear-gradient(135deg, rgba(24,47,39,0.96), rgba(20,31,42,0.96))"
                            : "linear-gradient(135deg, rgba(235,247,239,0.98), rgba(244,250,246,0.98))",
                        border: (th) => th.palette.mode === "dark"
                            ? "1px solid rgba(73,188,144,0.24)"
                            : "1px solid rgba(0, 121, 107, 0.12)",
                        boxShadow: theme.palette.mode === "dark"
                            ? "0 18px 50px rgba(0,0,0,0.28)"
                            : "0 20px 60px rgba(31, 94, 56, 0.10)",
                    }}
                >
                    <Grid container spacing={3} alignItems="center">
                        <Grid item xs={12} md={7}>
                            <Chip
                                icon={<SavingsOutlinedIcon />}
                                label={t("budget_recipes.landing_eyebrow") || "Με το budget σου"}
                                sx={{
                                    mb: 2,
                                    fontWeight: 700,
                                    bgcolor: theme.palette.mode === "dark"
                                        ? "rgba(73,188,144,0.15)"
                                        : "rgba(0,158,117,0.10)",
                                }}
                            />
                            <Typography
                                variant={isMobile ? "h5" : "h4"}
                                sx={{ fontWeight: 900, lineHeight: 1.1, mb: 1.25, letterSpacing: "-0.02em" }}
                            >
                                {t("budget_recipes.landing_title") || "Cook for your people, within your budget"}
                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{ color: "text.secondary", maxWidth: 620, mb: 2.25 }}
                            >
                                {t("budget_recipes.landing_body") ||
                                    "Find recipe suggestions based on how many people you want to cook for and how much you want to spend, using InCooking's real pricing logic."}
                            </Typography>
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2.5 }}>
                                <Chip icon={<Groups2Icon />} label={t("budget_recipes.landing_people_chip") || "People-first matching"} />
                                <Chip icon={<PaidOutlinedIcon />} label={t("budget_recipes.landing_budget_chip") || "Budget-aware pricing"} />
                                <Chip icon={<SavingsOutlinedIcon />} label={t("budget_recipes.landing_value_chip") || "Premium product feel"} />
                            </Box>
                            <Button
                                variant="contained"
                                size={isMobile ? "medium" : "large"}
                                endIcon={<ArrowForwardIcon />}
                                onClick={handleGoBudgetRecipes}
                                sx={{
                                    borderRadius: 999,
                                    px: 3.2,
                                    py: 1.1,
                                    fontWeight: 700,
                                    textTransform: "none",
                                }}
                            >
                                {t("budget_recipes.landing_cta") || "Find recipes"}
                            </Button>
                        </Grid>
                        <Grid item xs={12} md={5}>
                            <Card
                                onClick={handleGoBudgetRecipes}
                                sx={{
                                    borderRadius: 4,
                                    cursor: "pointer",
                                    background: theme.palette.mode === "dark"
                                        ? "linear-gradient(180deg, rgba(17,27,37,0.98), rgba(29,50,43,0.98))"
                                        : "linear-gradient(180deg, rgba(255,255,255,0.98), rgba(243,250,245,0.98))",
                                    "&:hover": {
                                        transform: "translateY(-3px)",
                                        boxShadow: 6,
                                    },
                                    transition: "all 0.18s",
                                }}
                            >
                                <CardContent>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1.5 }}>
                                        {t("budget_recipes.landing_preview_title") || "Budget recipe finder"}
                                    </Typography>
                                    <Grid container spacing={1.25}>
                                        <Grid item xs={6}>
                                            <Box sx={{ p: 1.5, borderRadius: 3, bgcolor: "rgba(76,175,80,0.08)" }}>
                                                <Typography variant="caption" color="text.secondary">
                                                    {t("budget_recipes.landing_preview_people") || "People"}
                                                </Typography>
                                                <Typography variant="h6" sx={{ fontWeight: 900 }}>4</Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Box sx={{ p: 1.5, borderRadius: 3, bgcolor: "rgba(33,150,243,0.08)" }}>
                                                <Typography variant="caption" color="text.secondary">
                                                    {t("budget_recipes.landing_preview_budget") || "Budget"}
                                                </Typography>
                                                <Typography variant="h6" sx={{ fontWeight: 900 }}>€20</Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Box sx={{ p: 1.75, borderRadius: 3, bgcolor: "rgba(255,255,255,0.55)" }}>
                                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                    {t("budget_recipes.landing_preview_result") || "Find the recipes that fit your target first, then compare value per person."}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </Box>

                {/* FEATURE CARDS SECTION */}
                <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                        <Card
                            onClick={handleGoMealPlan}
                            sx={{
                                height: "100%",
                                borderRadius: 3,
                                boxShadow: 2,
                                cursor: "pointer",
                                transition: "all 0.18s",
                                "&:hover": {
                                    transform: "translateY(-4px)",
                                    boxShadow: 6,
                                },
                            }}
                        >
                            <CardContent>
                                <RestaurantIcon color="success" sx={{ fontSize: 32, mb: 1 }} />
                                <Typography variant="h6" fontWeight={700} gutterBottom>
                                    {t("meal_plan") || "Smart meal planning"}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {t("feature_meal_planning_desc") ||
                                        "Combine your household, diet and budget to get realistic plans that you can actually follow."}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Card
                            onClick={handleGoShopping}
                            sx={{
                                height: "100%",
                                borderRadius: 3,
                                boxShadow: 2,
                                cursor: "pointer",
                                transition: "all 0.18s",
                                "&:hover": {
                                    transform: "translateY(-4px)",
                                    boxShadow: 6,
                                },
                            }}
                        >
                            <CardContent>
                                <ShoppingCartIcon color="error" sx={{ fontSize: 32, mb: 1 }} />
                                <Typography variant="h6" fontWeight={700} gutterBottom>
                                    {t("feature_shopping_optimization") || "Optimized shopping list"}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {t("feature_shopping_optimization_desc") ||
                                        "Generate shopping lists with quantities and prices per supermarket to stay within your budget."}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Card
                            onClick={handleGoMyRecipes}
                            sx={{
                                height: "100%",
                                borderRadius: 3,
                                boxShadow: 2,
                                cursor: "pointer",
                                transition: "all 0.18s",
                                "&:hover": {
                                    transform: "translateY(-4px)",
                                    boxShadow: 6,
                                },
                            }}
                        >
                            <CardContent>
                                <MenuBookIcon color="secondary" sx={{ fontSize: 32, mb: 1 }} />
                                <Typography variant="h6" fontWeight={700} gutterBottom>
                                    {t("feature_own_recipes") || "Your recipes, your style"}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {t("feature_own_recipes_desc") ||
                                        "Save your own recipes, mark favorites and share them with the community when you’re ready."}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
};

export default ClientLandingPage;
